import React, { useState, useEffect } from 'react';
import { X, History, RotateCw, AlertCircle, CheckCircle2, Clock, Coins, Cpu, Eye, Layers } from 'lucide-react';
import { getTenderAgentHistory } from '../../../services/tenderAgentApi';

export default function TenderRunHistoryModal({ onClose }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const res = await getTenderAgentHistory({ limit: 30 });
      if (res?.success && Array.isArray(res.data)) {
        setRuns(res.data);
      }
    } catch (err) {
      console.error('Erreur chargement historique des runs:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? dateStr
      : d.toLocaleString('fr-FR', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'RUNNING':
        return <span className="ao-badge ao-badge-running">En cours</span>;
      case 'COMPLETED':
      case 'SUCCESS':
        return <span className="ao-badge ao-badge-success">Succès</span>;
      case 'FAILED':
      case 'ERROR':
        return <span className="ao-badge ao-badge-urgent">Échec</span>;
      default:
        return <span className="ao-badge ao-badge-neutral">{status || 'Inconnu'}</span>;
    }
  };

  return (
    <div className="ao-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="ao-modal-card ao-modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="ao-modal-header">
          <div className="flex items-center space-x-2.5">
            <div className="ao-modal-icon-badge">
              <History size={20} className="text-amber-500" />
            </div>
            <div>
              <h3 className="ao-modal-title">Historique des Exécutions du Tender Agent</h3>
              <p className="ao-modal-subtitle">Traçabilité complète des scans TUNEPS, tokens consommés et coûts estimés</p>
            </div>
          </div>
          <button type="button" className="ao-modal-close-btn" onClick={onClose} aria-label="Fermer la modale">
            <X size={20} />
          </button>
        </div>

        <div className="ao-modal-body">
          {loading ? (
            <div className="ao-loading-state">
              <RotateCw size={28} className="animate-spin text-amber-500 mb-2" />
              <p>Chargement de l'historique des exécutions...</p>
            </div>
          ) : runs.length === 0 ? (
            <div className="ao-empty-box">
              <History size={40} className="text-gray-300 mb-2" />
              <p className="font-semibold text-gray-700">Aucune exécution enregistrée pour le moment.</p>
              <p className="text-xs text-gray-400">Lancez une collecte manuelle ou activez le planificateur automatique.</p>
            </div>
          ) : (
            <div className="ao-table-wrapper">
              <table className="ao-table">
                <thead>
                  <tr>
                    <th>Date & Heure</th>
                    <th>Mode</th>
                    <th>Déclencheur</th>
                    <th>Statut</th>
                    <th>Marchés Traités</th>
                    <th>Durée</th>
                    <th>Coût USD</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r._id || r.runId}>
                      <td className="font-semibold text-xs text-gray-800">
                        {formatDate(r.startedAt || r.createdAt)}
                      </td>
                      <td>
                        <span className="ao-source-badge">{r.mode}</span>
                      </td>
                      <td className="text-xs text-gray-600">
                        {r.triggeredBy === 'AUTOMATIC' ? '⏰ Automatique' : '👤 Manuel Admin'}
                      </td>
                      <td>{getStatusBadge(r.status)}</td>
                      <td className="text-xs">
                        <span className="font-bold text-gray-800">{r.totalDiscovered || 0}</span> découverts
                        <br />
                        <span className="font-bold text-emerald-600">{r.totalIngested || 0}</span> ingérés
                      </td>
                      <td className="text-xs text-gray-600">
                        {r.durationSeconds ? `${r.durationSeconds.toFixed(1)} s` : '—'}
                      </td>
                      <td className="text-xs font-bold text-gray-800">
                        ${Number(r.estimatedCostUsd || 0).toFixed(4)}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="ao-btn-inspect"
                          onClick={() => setSelectedRun(r)}
                          title="Voir les détails de l'exécution"
                        >
                          <Eye size={14} className="mr-1" />
                          <span>Détails</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Tiroir d'inspection d'un run */}
          {selectedRun && (
            <div className="ao-run-drawer">
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center space-x-2">
                  <span className="font-bold text-sm text-gray-900">Détails de l'Exécution</span>
                  <code className="ao-code-pill">{selectedRun.runId}</code>
                </div>
                <button
                  type="button"
                  className="ao-btn ao-btn-ghost text-xs"
                  onClick={() => setSelectedRun(null)}
                >
                  Masquer
                </button>
              </div>

              <div className="grid grid-cols-4 gap-3 text-xs mb-3">
                <div className="ao-spec-item">
                  <span className="ao-spec-label">Tokens Consommés</span>
                  <span className="ao-spec-val font-bold">{(selectedRun.tokensConsumed || 0).toLocaleString()}</span>
                </div>
                <div className="ao-spec-item">
                  <span className="ao-spec-label">Filtrés Hors-Sujet</span>
                  <span className="ao-spec-val font-bold">{selectedRun.totalFiltered || 0}</span>
                </div>
                <div className="ao-spec-item">
                  <span className="ao-spec-label">Analysés par LLM</span>
                  <span className="ao-spec-val font-bold">{selectedRun.totalAnalyzedLlm || 0}</span>
                </div>
                <div className="ao-spec-item">
                  <span className="ao-spec-label">Mises à Jour (Reports)</span>
                  <span className="ao-spec-val font-bold">{selectedRun.totalUpdated || 0}</span>
                </div>
              </div>

              {selectedRun.errors?.length > 0 && (
                <div className="ao-error-callout">
                  <div className="font-semibold mb-1 flex items-center">
                    <AlertCircle size={14} className="mr-1.5" /> Erreurs signalées durant la collecte :
                  </div>
                  <ul className="list-disc list-inside">
                    {selectedRun.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
